import { Upload } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useIntl } from '../../hooks';
import { zodResolver } from '../../lib/zod-resolver.js';
import { firmwareFormSchema, type FirmwareFormValues } from '../../validations/firmware-form.js';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '../ui';
import { DeviceModelsSelect } from './DeviceModelsSelect.js';

interface AddFirmwareDialogProps {
  open: boolean;
  isSubmitting: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: FirmwareFormValues) => Promise<void | string>;
}

const INITIAL_VALUES: Partial<FirmwareFormValues> = {
  version: '',
  deviceModels: [],
};

export function AddFirmwareDialog({
  open,
  isSubmitting,
  onOpenChange,
  onSubmit,
}: AddFirmwareDialogProps) {
  const intl = useIntl();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    setValue,
    watch,
    formState: { errors, isValid },
  } = useForm<FirmwareFormValues>({
    resolver: zodResolver(firmwareFormSchema),
    defaultValues: INITIAL_VALUES,
    mode: 'onChange',
  });

  const deviceModels = watch('deviceModels') ?? [];

  useEffect(() => {
    if (open) {
      reset(INITIAL_VALUES);
      setSelectedFile(null);
      clearErrors();
    }
  }, [open, reset, clearErrors]);

  const handleFileChange = useCallback(
    (file: File | null) => {
      setSelectedFile(file);
      if (file) {
        setValue('file', file, { shouldValidate: true });
      }
    },
    [setValue],
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    handleFileChange(file);
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      const file = e.dataTransfer.files?.[0] || null;
      handleFileChange(file);
    },
    [handleFileChange],
  );

  const handleDeviceModelsChange = (models: string[]) => {
    setValue('deviceModels', models);
  };

  const onFormSubmit = handleSubmit(async (values) => {
    clearErrors('root');
    const result = await onSubmit(values);

    if (typeof result === 'string' && result.length) {
      setError('root', {
        type: 'server',
        message: result,
      });
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{intl.formatMessage({ id: 'firmwares.dialog.title' })}</DialogTitle>
          <DialogDescription>
            {intl.formatMessage({ id: 'firmwares.dialog.description' })}
          </DialogDescription>
        </DialogHeader>
        <form className="space-y-4" onSubmit={onFormSubmit}>
          <div className="grid gap-2">
            <Label htmlFor="version">
              {intl.formatMessage({ id: 'firmwares.dialog.version' })}
            </Label>
            <Input
              id="version"
              autoComplete="off"
              placeholder={intl.formatMessage({ id: 'firmwares.dialog.versionPlaceholder' })}
              {...register('version')}
            />
            <p className="text-xs text-foreground">
              {intl.formatMessage({ id: 'firmwares.dialog.versionHint' })}
            </p>
            {errors.version && <p className="text-sm text-destructive">{errors.version.message}</p>}
          </div>

          <div className="grid gap-2">
            <Label>{intl.formatMessage({ id: 'firmwares.dialog.file' })}</Label>
            <div
              role="button"
              tabIndex={0}
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                dragActive ? 'border-accent bg-accent/10' : 'border-border hover:border-accent/50'
              }`}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".bin,.hex,.fw"
                className="hidden"
                onChange={handleInputChange}
              />
              <Upload className="h-8 w-8 mx-auto mb-2 text-foreground" />
              {selectedFile ? (
                <div>
                  <p className="text-sm font-medium">{selectedFile.name}</p>
                  <p className="text-xs text-foreground">
                    {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                  </p>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-foreground">
                    {intl.formatMessage({ id: 'firmwares.dialog.dropzone' })}
                  </p>
                  <p className="text-xs text-foreground mt-1">
                    {intl.formatMessage({ id: 'firmwares.dialog.allowedFormats' })}
                  </p>
                </div>
              )}
            </div>
            {errors.file && <p className="text-sm text-destructive">{errors.file.message}</p>}
          </div>

          <div className="grid gap-2">
            <Label>{intl.formatMessage({ id: 'firmwares.dialog.deviceModels' })}</Label>
            <DeviceModelsSelect
              value={deviceModels}
              onChange={handleDeviceModelsChange}
              disabled={isSubmitting}
            />
            <p className="text-xs text-foreground">
              {intl.formatMessage({ id: 'firmwares.dialog.deviceModelsHint' })}
            </p>
          </div>

          {errors.root && <div className="text-sm text-destructive">{errors.root.message}</div>}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {intl.formatMessage({ id: 'common.cancel' })}
            </Button>
            <Button type="submit" disabled={!isValid || isSubmitting}>
              {isSubmitting
                ? intl.formatMessage({ id: 'firmwares.dialog.uploading' })
                : intl.formatMessage({ id: 'firmwares.dialog.upload' })}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
